import React, { useMemo } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import {
    abTests,
    breadcrumbs,
    headerType,
    localeSettings,
    currencies,
    locales,
    rollouts,
    notificationSoundEnabled,
    title,
    user,
    categories,
    showBuyerNavigation,
    userAgent,
    requestUrl,
    isFullWidth,
    showNaviconButton,
    subCategoryId,
} from '../../PropTypes';
import { deserializeCurrencies, parseBalance, getCurrencyObj } from '../currencySerializers';
import { deserializeLocales } from '../localeSerializers';
import { PricingDataProvider } from '../../contexts/PricingDataContext';
import { HeaderContext } from '../../modern/context/HeaderContext';

const withContext = (Composite) => {
    const ContextWrapper = (props) => {
        const {
            displayedLocale,
            selectedCurrencyRate,
            supportedCurrencies = [],
            currencies,
            supportedLanguages = [],
            locales,
            user = {},
            rollouts = {},
            abTests = {},
            headerType,
            categories,
            showBuyerNavigation,
            requestUrl,
            notificationSoundEnabled,
            isFullWidth = false,
            showNaviconButton = true,
            priceUpfrontData,
            subCategoryId,
        } = props;

        const { locale, currency, userAgent, referrer, pageCtxId } = getContext();

        const availableCurrencies = useMemo(
            () => (supportedCurrencies.length ? supportedCurrencies : deserializeCurrencies(currencies)),
            [supportedCurrencies, currencies]
        );
        const currencyObj = useMemo(() => getCurrencyObj(currency, currencies) || {}, [currency, currencies]);
        const currencyRate = selectedCurrencyRate || currencyObj.exchange_rate || null;
        const userBalance = useMemo(
            () =>
                parseBalance({
                    balance: user.balance,
                    supportedCurrencies: availableCurrencies,
                    currencyRate,
                    currency,
                }),
            [user.balance, availableCurrencies, currencyRate, currency]
        );

        const contextValue = useMemo(
            () => ({
                locale,
                displayedLocale,
                currency: currencyObj,
                supportedCurrencies: availableCurrencies,
                supportedLanguages: supportedLanguages.length ? supportedLanguages : deserializeLocales(locales),
                user: {
                    ...user,
                    balance: userBalance,
                },
                rollouts,
                notificationSoundEnabled,
                abTests,
                headerType,
                categories,
                showBuyerNavigation,
                userAgent,
                requestUrl,
                isFullWidth,
                referrer,
                pageCtxId,
                showNaviconButton,
                subCategoryId,
            }),
            [
                locale,
                displayedLocale,
                currencyObj,
                availableCurrencies,
                supportedLanguages,
                locales,
                user,
                userBalance,
                rollouts,
                notificationSoundEnabled,
                abTests,
                headerType,
                categories,
                showBuyerNavigation,
                userAgent,
                requestUrl,
                isFullWidth,
                referrer,
                pageCtxId,
                showNaviconButton,
                subCategoryId,
            ]
        );

        return (
            <HeaderContext.Provider value={contextValue}>
                <PricingDataProvider priceUpfrontData={priceUpfrontData}>
                    <Composite {...props} />
                </PricingDataProvider>
            </HeaderContext.Provider>
        );
    };

    const MemoizedContextWrapper = React.memo(ContextWrapper);

    MemoizedContextWrapper.displayName = `withContext(${Composite.displayName || Composite.name || 'Component'})`;

    MemoizedContextWrapper.propTypes = {
        headerType,
        user,
        title,
        breadcrumbs,
        supportedCurrencies: localeSettings.supportedCurrencies,
        supportedLanguages: localeSettings.supportedLanguages,
        selectedCurrencyRate: localeSettings.currencyRate,
        locale: localeSettings.locale,
        locales,
        currencies,
        displayedLocale: localeSettings.displayedLocale,
        rollouts,
        notificationSoundEnabled,
        abTests,
        categories,
        showBuyerNavigation,
        userAgent,
        requestUrl,
        isFullWidth,
        showNaviconButton,
        subCategoryId,
    };

    return MemoizedContextWrapper;
};

export default withContext;
